import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Skeleton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export interface ColumnConfig<T> {
  label: string;
  render: (item: T) => React.ReactNode;
}

export interface LookupTableProps<T extends { id: number }> {
  items: T[];
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  loading: boolean;
  columns: ColumnConfig<T>[];
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  /** Optional: Test/Verify-Aktion pro Zeile */
  onTest?: (item: T) => void;
}

function LookupTable<T extends { id: number }>({
  items,
  onAdd,
  onEdit,
  onDelete,
  loading,
  columns,
  onMoveUp,
  onMoveDown,
  onTest,
}: LookupTableProps<T>) {
  const sortable = !!(onMoveUp && onMoveDown);

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" onClick={onAdd}>
            Hinzufügen
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {sortable && <TableCell sx={{ width: 100 }}>Reihenfolge</TableCell>}
                {columns.map((col, idx) => (
                  <TableCell key={idx}>{col.label}</TableCell>
                ))}
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (sortable ? 2 : 1)} align="center">
                    Keine Einträge gefunden
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={item.id} hover>
                    {sortable && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          onClick={() => onMoveUp!(index)}
                          disabled={index === 0}
                          title="Nach oben"
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onMoveDown!(index)}
                          disabled={index === items.length - 1}
                          title="Nach unten"
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((col, idx) => (
                      <TableCell key={idx}>{col.render(item)}</TableCell>
                    ))}
                    <TableCell align="right">
                      {onTest && (
                        <IconButton
                          size="small"
                          onClick={() => onTest(item)}
                          title="Verbindung testen"
                          color="success"
                        >
                          <NetworkCheckIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => onEdit(item)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDelete(item)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default LookupTable;
